import { AgentStarterService } from '@services/agent-starter.service';
import { PortConfig } from '@modules/agent/models/port-config.model';
import { environment } from '@environment';

const DEFAULT_HOST = 'https://local.testsigmaagent.com';
const DEFAULT_PORT = 8484;
const DEFAULT_URL = `${DEFAULT_HOST}:${DEFAULT_PORT}`;
const DEFAULT_DEBUGGER_PORT = import.meta.env.VITE_DEBUGGER_PORT;

// Cache for PortConfig to avoid multiple API calls
let portConfigCache: PortConfig | null = null;
let portConfigPromise: Promise<PortConfig> | null = null;

/**
 * Fetches PortConfig and caches it for subsequent calls
 * @returns Promise<PortConfig>
 */
export const fetchPortConfig = async (): Promise<PortConfig | null> => {
  // Return cached value if available
  if (portConfigCache) {
    return portConfigCache;
  }

  // Return existing promise if fetch is in progress
  if (portConfigPromise) {
    try {
      portConfigCache = await portConfigPromise;
      return portConfigCache;
    } catch (error) {
      // If fetch fails, clear promise and return null
      portConfigPromise = null;
      return null;
    }
  }

  // Start new fetch
  portConfigPromise = AgentStarterService.getPorts();
  try {
    portConfigCache = await portConfigPromise;
    return portConfigCache;
  } catch (error) {
    portConfigPromise = null;
    return null;
  }
};

/**
 * Gets the agent URL with port from PortConfig if available, otherwise uses default
 * @returns Promise<string> - The agent URL (e.g., 'https://local.testsigmaagent.com:8484')
 */
export const getAgentUrl = async (): Promise<string> => {
  const portConfig = await fetchPortConfig();

  if (portConfig?.httpsPort) {
    return `${DEFAULT_HOST}:${portConfig.httpsPort}`;
  }

  // Fallback to default URL from environment or computed default
  return environment.driverActionHostAgent || DEFAULT_URL;
};

/**
 * Gets the agent URL synchronously if PortConfig is already cached, otherwise returns default
 * Use this when you need a synchronous value and can accept the default if not cached yet
 * @returns string - The agent URL
 */
export const getAgentUrlSync = (): string => {
  if (portConfigCache?.httpsPort) {
    return `${DEFAULT_HOST}:${portConfigCache.httpsPort}`;
  }

  return environment.driverActionHostAgent || DEFAULT_URL;
};

/**
 * Gets the drifter server port from PortConfig if available, otherwise uses VITE_DEBUGGER_PORT
 * @returns Promise<string | undefined> - The drifter server port
 */
export const getDrifterPort = async (): Promise<string | undefined> => {
  const portConfig = await fetchPortConfig();

  if (portConfig?.drifterServerPort) {
    return String(portConfig.drifterServerPort);
  }

  // Fallback to environment variable
  return DEFAULT_DEBUGGER_PORT;
};

/**
 * Gets the agent debugger port from PortConfig if available, otherwise uses VITE_DEBUGGER_PORT
 * This is the port where the gRPC debugger notification service runs
 * @returns Promise<string | undefined> - The agent debugger port
 */
export const getAgentDebuggerPort = async (): Promise<string | undefined> => {
  const portConfig = await fetchPortConfig();

  if (portConfig?.debuggerPort) {
    return String(portConfig.debuggerPort);
  }

  // Fallback to environment variable
  return DEFAULT_DEBUGGER_PORT;
};

/**
 * Gets the drifter server port synchronously if PortConfig is already cached, otherwise returns VITE_DEBUGGER_PORT
 * Use this when you need a synchronous value and can accept the default if not cached yet
 * @returns string | undefined - The drifter server port
 */
export const getDrifterPortSync = (): string | undefined => {
  if (portConfigCache?.drifterServerPort) {
    return String(portConfigCache.drifterServerPort);
  }

  // Fallback to environment variable
  return DEFAULT_DEBUGGER_PORT;
};

/**
 * Clears the cached PortConfig (useful for testing or when ports change)
 */
export const clearPortConfigCache = (): void => {
  portConfigCache = null;
  portConfigPromise = null;
};
